import React from "react";
import { motion } from "framer-motion";
import Container from "../components/Container";
import romanoImg from "../assets/romano_picture.png";
import rivettiImg from "../assets/rivetti_picture.jpg";
import faroLogo from "../assets/faro.png";

/* ─── Palette (alineada con HomePage) ─── */
const C = {
  navy: "#0B1F3A",
  navyLight: "#112B4D",
  navyMid: "#0E2545",
  amber: "#FFC857",
  amberDark: "#E6B04E",
  paper: "#F5F2EA",
  white: "#F5F7FA",
  gray: "#6B7280",
  grayLight: "#9CA3AF",
  grayDark: "#4B5563",
};

const SERIF = "'Fraunces', 'Playfair Display', Georgia, serif";
const SANS = "'Inter', 'DM Sans', system-ui, sans-serif";
const MONO = "'JetBrains Mono', 'IBM Plex Mono', ui-monospace, monospace";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, delay, ease: "easeOut" },
});

function AmberRule({ width = "52px", className = "" }) {
  return <div className={`h-0.5 rounded-full ${className}`} style={{ width, background: C.amber }} />;
}

/* ─── TeamCard — vertical con foto circular ─── */
function TeamCard({ photo, name, role, bio, delay = 0 }) {
  return (
    <motion.div {...fadeUp(delay)}
      className="rounded-2xl p-6 md:p-7 flex flex-col items-center text-center"
      style={{
        background: C.navyLight,
        border: `1px solid rgba(255,200,87,0.18)`,
        boxShadow: "0 8px 24px rgba(11,31,58,0.18), 0 1px 3px rgba(11,31,58,0.08)",
      }}>
      <div className="w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden mb-4"
        style={{ border: `2px solid ${C.amber}`, boxShadow: `0 0 0 4px rgba(255,200,87,0.18)` }}>
        <img src={photo} alt={name} className="w-full h-full"
          style={{ objectFit: "cover", objectPosition: "center top" }} />
      </div>
      <h3 className="text-[19px] md:text-[21px] font-bold leading-tight" style={{ color: C.white, fontFamily: SANS }}>
        {name}
      </h3>
      <span className="mt-1.5 text-[10.5px] font-bold uppercase tracking-[0.22em]"
        style={{ color: C.amber, fontFamily: MONO }}>
        {role}
      </span>
      <div className="w-11 h-px my-3.5" style={{ background: C.amber }} />
      <p className="text-[13.5px] md:text-[14px] leading-[1.65]" style={{ color: C.grayLight, fontFamily: SANS }}>
        {bio}
      </p>
    </motion.div>
  );
}

export default function AboutPage({ onNavigate, language }) {
  const en = language === "en";

  const team = [
    {
      photo: romanoImg,
      name: "Santiago Romano",
      role: "Advanced Data Scientist",
      bio: en
        ? "Industrial Engineer and MSc in Management & Analytics (Universidad Torcuato Di Tella) with 4+ years of experience in software development, data science and machine learning. Experience developing and managing artificial intelligence solutions in production environments across diverse industries. Applied experience in retail building large-scale demand forecasting models and automated replenishment systems."
        : "Ingeniero Industrial y Magíster en Management & Analytics (Universidad Torcuato Di Tella) con más de 4 años de experiencia en desarrollo de software, data science y machine learning. Experiencia en el desarrollo y gestión de soluciones de inteligencia artificial en entornos productivos para diversas industrias. Experiencia aplicada en retail desarrollando modelos de predicción de demanda a escala y sistemas de reposición automática.",
    },
    {
      photo: rivettiImg,
      name: "Luciano Rivetti",
      role: "Advanced Data Scientist",
      bio: en
        ? "PhD in Physics with 6+ years of experience researching and developing artificial intelligence models at world-class institutions like Harvard University and Instituto Balseiro. Author of scientific publications with 100+ citations. Applied experience in consulting and private industry, developing high-impact AI solutions in production environments."
        : "Doctor en Física con más de 6 años de experiencia en investigación y desarrollo de modelos de inteligencia artificial en instituciones de excelencia como Harvard University y el Instituto Balseiro. Autor de publicaciones científicas con más de 100 citas. Experiencia aplicada en consultoría e industria privada, desarrollando soluciones de IA de alto impacto en entornos productivos.",
    },
  ];

  return (
    <>
      {/* ═══ HERO + EQUIPO — toda la página en una sola sección navy ═══ */}
      <section className="relative pt-12 md:pt-16 pb-16 md:pb-20 overflow-hidden"
        style={{ background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 100%)` }}>
        <div className="absolute top-0 left-0 w-full h-1" style={{ background: C.amber }} />
        <div className="absolute top-0 right-0 w-2 md:w-3 h-full pointer-events-none" style={{ background: C.amber, opacity: 0.15 }} />
        {/* FARO watermark */}
        <div className="absolute top-0 right-0 w-[60%] h-full pointer-events-none select-none" style={{ transform: "scaleX(-1)" }}>
          <img src={faroLogo} alt="" className="w-full h-full object-contain"
            style={{ opacity: 0.04, filter: "brightness(2)" }} draggable={false} />
        </div>
        <Container className="relative z-10">
          {/* Header */}
          <motion.div {...fadeUp(0)} className="max-w-3xl mx-auto text-center mb-8 md:mb-10">
            <h1 className="text-[26px] md:text-[34px] lg:text-[40px] font-bold leading-[1.15]"
              style={{ color: C.white, fontFamily: SANS, letterSpacing: "-0.015em" }}>
              {en ? (
                <>Behind FARO there's a team that{" "}
                  <span style={{ color: C.amber, fontFamily: SERIF, fontStyle: "italic", fontWeight: 500 }}>understands</span>{" "}
                  your business.</>
              ) : (
                <>Detrás de FARO hay un equipo que{" "}
                  <span style={{ color: C.amber, fontFamily: SERIF, fontStyle: "italic", fontWeight: 500 }}>entiende</span>{" "}
                  tu negocio.</>
              )}
            </h1>
            <AmberRule width="72px" className="mt-5 mx-auto" />
            <p className="mt-6 text-[14px] md:text-[15px] leading-[1.7] max-w-2xl mx-auto" style={{ color: C.grayLight, fontFamily: SANS }}>
              {en
                ? "FARO is built by specialists in artificial intelligence, data science and software development, with real experience across multiple industries. Every model, every technical decision and every line of code is backed by people who understand both the technology and the problem it solves."
                : "FARO está construido por especialistas en inteligencia artificial, ciencia de datos y desarrollo de software con experiencia real en varias industrias. Cada modelo, cada decisión técnica y cada línea de código está respaldada por quienes entienden tanto la tecnología como el problema que resuelve."}
            </p>
          </motion.div>

          {/* Cards equipo */}
          <div className="grid gap-6 md:grid-cols-2 max-w-5xl mx-auto">
            {team.map((m, i) => (
              <TeamCard key={m.name} {...m} delay={i * 0.08} />
            ))}
          </div>

          {/* CTA inline — debajo de las cards */}
          <motion.div {...fadeUp(0.2)} className="flex justify-center mt-8 md:mt-10">
            <button onClick={() => onNavigate("contact")}
              className="rounded-xl text-sm md:text-base px-7 py-3 font-bold transition-all hover:-translate-y-0.5"
              style={{ background: C.amber, color: C.navy, fontFamily: SANS, boxShadow: "0 4px 24px rgba(255,200,87,0.35)" }}>
              {en ? "Get Started →" : "Comenzar →"}
            </button>
          </motion.div>
        </Container>
      </section>
    </>
  );
}
